/**
 * The logistic growth model: dN/dt = rN(1 − N/K), whose solution is
 * N(t) = K ÷ (1 + ((K − N₀) ÷ N₀) × e^(−rt)).
 *
 * Valid for N₀ > 0, K > 0 and r > 0. N₀ above K is allowed: the population then falls
 * back to K from above and never has an inflection point after t = 0.
 */

export interface LogisticParams {
  /** Starting size N₀. */
  n0: number;
  /** Carrying capacity K. */
  k: number;
  /** Intrinsic rate of increase r, per time unit. */
  r: number;
}

export type LogisticProblem = "n0" | "k" | "r";

/** The first invalid parameter, or null when the model can be evaluated. */
export function logisticProblem(p: LogisticParams): LogisticProblem | null {
  if (!(Number.isFinite(p.n0) && p.n0 > 0)) {
    return "n0";
  }
  if (!(Number.isFinite(p.k) && p.k > 0)) {
    return "k";
  }
  if (!(Number.isFinite(p.r) && p.r > 0)) {
    return "r";
  }
  return null;
}

/** (K − N₀) ÷ N₀: the constant in the denominator of the solution. */
export function logisticOffset(p: LogisticParams): number {
  return (p.k - p.n0) / p.n0;
}

/** Population size at time t (t = 0 gives N₀). */
export function populationAt(p: LogisticParams, t: number): number {
  return p.k / (1 + logisticOffset(p) * Math.exp(-p.r * t));
}

/** dN/dt at population size n: rN(1 − N/K). */
export function growthRateAt(p: LogisticParams, n: number): number {
  return p.r * n * (1 - n / p.k);
}

/** Per-capita growth rate (1/N)(dN/dt) = r(1 − N/K) at size n. */
export function perCapitaRateAt(p: LogisticParams, n: number): number {
  return p.r * (1 - n / p.k);
}

/**
 * The time at which the population reaches `target`, solving the model for t:
 * t = ln(N(K − N₀) ÷ (N₀(K − N))) ÷ r. Negative when the curve passed that size before
 * t = 0 (a target below N₀). Null when it is never reached: a target at or below zero,
 * exactly K, on the other side of K from N₀, or when N₀ = K already.
 */
export function timeToReach(p: LogisticParams, target: number): number | null {
  if (!(Number.isFinite(target) && target > 0) || target === p.k) {
    return null;
  }
  const ratio = (target * (p.k - p.n0)) / (p.n0 * (p.k - target));
  if (!(ratio > 0 && Number.isFinite(ratio))) {
    return null;
  }
  return Math.log(ratio) / p.r;
}

/** The ratio inside the logarithm of `timeToReach`, for showing the working. */
export function reachRatio(p: LogisticParams, target: number): number {
  return (target * (p.k - p.n0)) / (p.n0 * (p.k - target));
}

export interface Inflection {
  /** Population size at the inflection: K ÷ 2. */
  size: number;
  /** Time of the inflection, or null when N₀ is already at or above K ÷ 2. */
  time: number | null;
  /** The steepest growth rate, rK ÷ 4. */
  maxRate: number;
}

export function inflectionOf(p: LogisticParams): Inflection {
  const size = p.k / 2;
  const time = p.n0 < size ? Math.log((p.k - p.n0) / p.n0) / p.r : null;
  return { size, time, maxRate: (p.r * p.k) / 4 };
}

/** The fraction of the way to K that "has arrived" for the chart's horizon. */
const HORIZON_SHARE = 0.99;

/** The time axis' natural length: until the curve is within 1% of K, plus a margin. */
export function chartHorizon(p: LogisticParams, t: number): number {
  const target = p.n0 < p.k ? p.k * HORIZON_SHARE : p.k * (2 - HORIZON_SHARE);
  const settle = timeToReach(p, target);
  if (settle === null || settle <= 0) {
    return Math.max(t, 10 / p.r);
  }
  return Math.max(settle * 1.1, Math.min(t, settle * 3));
}

export interface CurvePoint {
  t: number;
  n: number;
}

/** `count` + 1 evenly spaced points on the curve from 0 to `tMax`. */
export function curvePoints(
  p: LogisticParams,
  tMax: number,
  count = 80
): CurvePoint[] {
  return Array.from({ length: count + 1 }, (_, i) => {
    const t = (tMax * i) / count;
    return { t, n: populationAt(p, t) };
  });
}
